// The shop interiors in the farm market's look (design handoff 2026-10-27): the shop's picture dimmed a
// little, a cream panel with a wooden title, a wooden "Wyjdź" inside its bottom edge, and — for the
// clothing, pet and furniture shops — tabs over pages of tappable cards (shopGrid.mjs holds the numbers).
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { renderBackground } from '../render/world.js';
import { gamePanel, woodTitle, gameButton, gameCard, cardFeedback, pageArrows } from './gameUi.js';
import { FONT } from './gameUiStyle.mjs';
import { iconText } from './iconText.js';
import { tapGate } from './tapGate.mjs';
import { isInputLocked } from './inputLock.js';
import { isTouchDevice } from './isTouchDevice.js';
import { showToast } from './Toast.js';
import { messageFor } from '../api/errors.js';
import { SHOP_PANEL, CARD_W, CARD_H, gridLayout, cardCenter, pageOf, tabXs, shopKey, cardsSignature } from './shopGrid.mjs';

// Phones see the 1280×720 canvas at ~0.47: the card texts are drawn bigger there, like the market's.
const TOUCH = isTouchDevice();
const fpx = (n) => `${Math.round(n * (TOUCH ? 1.3 : 1))}px`;
const PRICE_GOLD = '#b8860b';

// Background, dim, panel, title and exit. Returns { layer, exit } — `layer` sits over the background.
export function shopRoom(scene, { bgKey, title, panel = SHOP_PANEL, onExit }) {
  renderBackground(scene, bgKey);
  const layer = scene.add.container(0, 0).setDepth(10);
  const dim = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.2).setOrigin(0, 0);
  const box = gamePanel(scene, panel.x, panel.y, panel.w, panel.h, { fillAlpha: 0.75 });   // owner: the room shows through a little
  const strip = woodTitle(scene, panel.x, panel.titleY, title, { fontSize: 24 });
  const exit = gameButton(scene, panel.x, panel.exitY, 200, 46, '{arrow_down} Wyjdź', onExit, { variant: 'wood', fontSize: 18 });
  layer.add([dim, box, strip, exit]);
  onShopKey(scene, ['Escape'], () => onExit?.());
  return { layer, exit };
}

// A key handler on the document (capture phase, before the browser scrolls or moves the focus), dropped when
// the scene shuts down. Not while typing in the chat or while a modal (the pet shop's kennel choice) is open.
function onShopKey(scene, keys, handle) {
  const onKey = (ev) => {
    if (!keys.includes(ev.key)) return;
    const el = document.activeElement;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
    if (isInputLocked(scene)) return;
    ev.preventDefault(); ev.stopPropagation();
    handle(ev);
  };
  document.addEventListener('keydown', onKey, true);
  scene.events.once('shutdown', () => document.removeEventListener('keydown', onKey, true));
}

// Tabs over a grid of cards. `cardsFor(tab)` returns plain card data:
//   { key, tex, name, sub?, foot, state: 'buy' | 'owned' | 'poor', onTap?, decorate?(img) }
// 'buy' cards pop and run onTap (a promise holds the other cards until it settles), 'poor' ones shake and
// say why, 'owned' ones don't react. The grid redraws when the data changes (after a purchase).
export function shopGrid(scene, layer, { tabs, tab: startTab = tabs[0].id, tabW = 142, tabStep = 150, cardsFor }) {
  const layout = gridLayout({ touch: TOUCH });
  const gate = tapGate();
  let tab = startTab, page = 0, pages = 1, body = null, shown = '';

  const tabButtons = tabXs(tabs.length, tabStep).map((x, i) => {
    const t = tabs[i];
    const b = gameButton(scene, x, SHOP_PANEL.tabY, tabW, 36, t.label, () => { tab = t.id; page = 0; draw(); }, { variant: 'secondary', fontSize: 16 });
    layer.add(b);
    return { id: t.id, b };
  });

  const draw = () => {
    body?.destroy(true);
    body = scene.add.container(0, 0);
    layer.add(body);
    for (const { id, b } of tabButtons) b.setVariant(id === tab ? 'wood' : 'secondary');
    const all = cardsFor(tab);
    shown = cardsSignature(all);
    const view = pageOf(all, page, layout.perPage);
    page = view.page; pages = view.pages;
    const cards = scene.add.container(0, 0);
    body.add(cards);
    view.items.forEach((c, i) => cards.add(drawCard(scene, c, cardCenter(i, layout), gate, () => setTimeout(refresh, 0))));
    if (layout.scale !== 1) {           // phones: one row, scaled about its centre
      const cy = layout.rowY[0];
      cards.setScale(layout.scale).setPosition(SHOP_PANEL.x * (1 - layout.scale), cy * (1 - layout.scale));
    }
    if (pages > 1) {
      body.add(pageArrows(scene, SHOP_PANEL.x, SHOP_PANEL.arrowY, 499, page, pages, (p) => { page = p; draw(); }));
      body.add(iconText(scene, SHOP_PANEL.x, SHOP_PANEL.pageY, `${page + 1} / ${pages}`, { fontFamily: FONT, fontSize: fpx(15), color: '#555' }).setOrigin(0.5));
    }
  };
  draw();

  // A state push redraws only when what the cards show changed — not while a buy is still settling (its card
  // is mid-pop); the card asks again once the buy is done.
  const refresh = () => { if (!gate.busy && !gate.closed && cardsSignature(cardsFor(tab)) !== shown) draw(); };
  const unsub = gameState.on(refresh);

  // Keyboard (as in the market): ←/→ pages, Tab / Shift+Tab the tabs (Esc is shopRoom's).
  onShopKey(scene, ['ArrowLeft', 'ArrowRight', 'Tab'], (ev) => {
    if (gate.busy) return;
    const r = shopKey(ev.key, ev.shiftKey, { tabs: tabs.map((t) => t.id), tab, page, pages });
    if (r) { tab = r.tab; page = r.page; draw(); }
  });
  scene.events.once('shutdown', () => { gate.close(); unsub(); });
}

function drawCard(scene, c, { x: cx, y: cy }, gate, settled) {
  const card = gameCard(scene, cx, cy, CARD_W, CARD_H);
  const parts = [];
  const key = scene.textures.exists(c.tex) ? c.tex : 'plot_empty';
  if (scene.textures.exists(key)) {
    const img = scene.add.image(cx, cy - 40, key);
    const src = img.texture.getSourceImage();
    const k = Math.min(140 / src.width, 76 / src.height);
    img.setDisplaySize(src.width * k, src.height * k);
    parts.push(img, ...[].concat(c.decorate?.(img) ?? []));
  }
  // Over a sub-line the name stays on one line and shrinks to fit (phones draw it bigger). Without one (clothing)
  // a long name wraps to two lines, a bit lower, and shrinks if it still needs a third.
  const size = c.sub ? 17 : 16;
  const name = iconText(scene, cx, cy + (c.sub ? 14 : 20), c.name, { fontFamily: FONT, fontSize: fpx(size), color: '#000', fontStyle: 'bold', align: 'center',
    ...(c.sub ? {} : { wordWrap: { width: CARD_W - 20 } }) }).setOrigin(0.5);
  if (c.sub && name.width > CARD_W - 16) name.setScale((CARD_W - 16) / name.width);
  for (let px = size - 1; !c.sub && name.height > 2 * parseInt(fpx(size), 10) * 1.3 + 2 && px >= 12; px--) name.setFontSize(fpx(px));
  parts.push(name);
  if (c.sub) {
    const sub = iconText(scene, cx, cy + 36, c.sub, { fontFamily: FONT, fontSize: fpx(13), color: '#333' }).setOrigin(0.5);
    if (sub.width > CARD_W - 16) sub.setScale((CARD_W - 16) / sub.width);
    parts.push(sub);
  }
  parts.push(iconText(scene, cx, cy + CARD_H / 2 - 18, c.foot, { fontFamily: FONT, fontSize: fpx(15), color: PRICE_GOLD, fontStyle: 'bold' }).setOrigin(0.5));

  if (c.state === 'owned') { for (const p of parts) { p.x -= cx; p.y -= cy; card.add(p); } return card; }
  if (c.state === 'poor') {
    cardFeedback(scene, card, parts, null);
    card.on('pointerup', () => showToast(scene, messageFor({ error_code: 'INSUFFICIENT_MONEY' })));
    return card;
  }
  return cardFeedback(scene, card, parts, () => Promise.resolve(c.onTap?.()).finally(settled), { gate });
}

// 'buy' unless the price is beyond the purse.
export function buyState(price) {
  return (gameState.raw?.money ?? 0) >= (price ?? 0) ? 'buy' : 'poor';
}
